import { el, clear, toast } from '../dom';
import { achievementsApi, type Achievement } from '../game-api';
import { ApiError } from '../../core/network/api';
import type { GameCtx, ViewTeardown } from '../types';
import { gicon, richText, type IconName } from '../ui/icons';

export const TIER_COLOR: Record<string, string> = { bronze: '#cd7f32', silver: '#cbd2d9', gold: '#f4c04e', legendary: '#b56bff' };
export const TIER_LABEL: Record<string, string> = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', legendary: 'Legendary' };

// Only a couple of the ~60 achievements ship a bespoke badge; the rest 404. Rather
// than show a broken image, fall back to the category medallion, then to a sprite
// emblem by id-prefix (specific) then category (generic), so every row renders
// something meaningful.
const EMBLEM_BY_PREFIX: [string, IconName][] = [
  ['wood', 'wood'], ['tree', 'leaf'], ['ore', 'ore'], ['stone', 'stone'], ['shroom', 'mushroom'],
  ['harvest', 'leaf'], ['mobs', 'swords'], ['death', 'xmark'], ['arena', 'sword'], ['rating', 'trophy'],
  ['level', 'star'], ['coins', 'coin'], ['plots', 'seed'], ['quests', 'check'], ['explore', 'star'],
  ['nest', 'seed'], ['land', 'leaf'], ['fortune', 'coin'], ['green', 'seed'],
];
const EMBLEM_BY_CATEGORY: Record<string, IconName> = {
  gathering: 'wood', combat: 'swords', farming: 'leaf', exploration: 'star',
  trading: 'coin', arena: 'sword', collection: 'trophy', hidden: 'gear',
};
function emblemIcon(a: Achievement): IconName {
  for (const [p, e] of EMBLEM_BY_PREFIX) if (a.id.startsWith(p)) return e;
  return EMBLEM_BY_CATEGORY[a.category] ?? 'trophy';
}

const CATEGORY_LABEL: Record<string, string> = {
  gathering: '⛏️ Gathering', combat: '⚔️ Combat', farming: '🌾 Farming', arena: '🏟️ Arena',
  trading: '🪙 Wealth', collection: '📚 Dedication', exploration: '🧭 Exploration', hidden: '❓ Hidden',
};
const CATEGORY_ORDER = ['gathering', 'farming', 'combat', 'arena', 'trading', 'collection', 'exploration', 'hidden'];

type Filter = 'all' | 'unlocked' | 'locked';

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Awards — a Steam-achievements-style LIST (was a grid of medallions).
 *
 * One row per achievement, clearly separated: badge art at the left, name +
 * description in the middle, unlock state / unlock date and rewards at the right,
 * and a progress bar with `progress / target` for anything partially done.
 *
 * There is no global rarity %: the backend (`backend/src/game/achievements/`)
 * computes unlocks per-player and stores no aggregate counts, so per-achievement
 * progress is the honest equivalent — no invented numbers.
 *
 * Perf: ~60 rows, all built into one DocumentFragment per repaint, and badge art
 * is `loading=lazy` + `decoding=async` so off-screen rows cost nothing.
 */
/**
 * The badge art for one achievement, with its full fallback chain.
 *
 * Extracted from the row renderer because the farm's award popup
 * (`engine/award-popup.ts`) has to show the SAME art: only a couple of the ~60
 * achievements ship a bespoke webp, so a popup that pointed straight at
 * `/achievements/<id>.webp` would have shown a broken image for nearly every
 * award won. One chain, one place, both callers.
 *
 * `dimmed` greys the art for a locked achievement (never used by the popup —
 * it only ever shows things you just won).
 */
export function achievementBadge(a: Achievement, size: number, dimmed = false): HTMLElement {
  const ring = dimmed ? '#4a3524' : (TIER_COLOR[a.tier] ?? '#c9a84c');
  const dim = dimmed ? 'filter:grayscale(1) brightness(0.6);opacity:.75' : '';
  const art = el('img', 'w-full h-full object-cover rounded-md', {
    src: `/achievements/${a.id}.webp`,
    alt: '',
    loading: 'lazy',
    decoding: 'async',
    style: `image-rendering:pixelated;${dim}`,
  }) as HTMLImageElement;
  let triedCategory = false;
  art.addEventListener('error', () => {
    if (!triedCategory) { triedCategory = true; art.src = `/achievements/cat_${a.category}.webp`; return; }
    art.replaceWith(el('span', 'absolute inset-0 flex items-center justify-center', { style: dim }, gicon(emblemIcon(a), Math.round(size * 0.53))));
  });
  return el('div', 'relative shrink-0 rounded-md overflow-hidden flex items-center justify-center', {
    style: `width:${size}px;height:${size}px;background:radial-gradient(circle at 50% 38%, #3a2a1b, #241810);`
      + `border:2px solid ${ring};${dimmed ? '' : `box-shadow:0 0 12px ${ring}55`}`,
  }, art);
}

export function renderAchievements(root: HTMLElement, _ctx: GameCtx): ViewTeardown {
  clear(root);
  const header = el('div', 'mb-5');
  const list = el('div', 'flex flex-col gap-5 pb-8');
  root.append(header, list);

  let all: Achievement[] = [];
  let filter: Filter = 'all';

  // ─── One row ────────────────────────────────────────────────────────────────
  function row(a: Achievement): HTMLElement {
    const ring = a.unlocked ? (TIER_COLOR[a.tier] ?? '#c9a84c') : '#4a3524';
    // Badge art, in priority: bespoke per-achievement webp → category medallion
    // webp → emoji/sprite emblem. Keep every step webp (the PNG pack was converted).
    const badge = achievementBadge(a, 64, !a.unlocked);

    // Progress — shown whenever the goal isn't a simple one-shot, or is unfinished.
    const pct = a.target > 0 ? Math.min(100, Math.round((a.progress / a.target) * 100)) : (a.unlocked ? 100 : 0);
    const progress = a.unlocked ? null : el('div', 'mt-2 flex items-center gap-2', {},
      el('div', 'flex-1 h-1.5 rounded-full bg-surface-2 overflow-hidden border border-border', {},
        el('div', 'h-full bg-gold/70', { style: `width:${pct}%` })),
      el('span', 'font-mono text-[10px] text-text-dim shrink-0 tabular-nums', {},
        `${a.progress.toLocaleString()} / ${a.target.toLocaleString()}`));

    const rewards = [
      a.rewardSoft ? `🪙 ${a.rewardSoft}` : '',
      a.rewardPremium ? `🍄 ${a.rewardPremium}` : '',
      a.rewardXp ? `⭐ ${a.rewardXp} XP` : '',
    ].filter(Boolean).join('  ');

    const meta = el('div', 'shrink-0 flex flex-col items-end justify-center gap-1 text-right min-w-[92px]', {},
      a.unlocked
        ? el('span', 'inline-flex items-center gap-1 text-[11px] text-leaf', {}, gicon('check', 11), 'Unlocked')
        : el('span', 'text-[11px] text-text-dim/70', {}, `${pct}%`),
      a.unlocked && a.unlockedAt
        ? el('span', 'text-[10px] font-mono text-text-dim', {}, fmtDate(a.unlockedAt))
        : null,
      rewards
        ? el('span', `text-[10px] font-mono inline-flex items-center gap-1 flex-wrap justify-end ${a.unlocked ? 'text-coin' : 'text-text-dim/70'}`, {}, ...richText(rewards, 11))
        : null);

    return el('div',
      'flex items-center gap-3 sm:gap-4 p-3 rounded-lg border transition '
      + (a.unlocked ? 'border-border bg-surface/70 hover:border-gold/60' : 'border-border/60 bg-surface-2/40 hover:border-border'),
      { style: a.unlocked ? `border-left:3px solid ${ring}` : 'border-left:3px solid transparent' },
      badge,
      el('div', 'flex-1 min-w-0', {},
        el('div', 'flex items-baseline gap-2 flex-wrap', {},
          el('span', 'text-sm font-medium ' + (a.unlocked ? 'text-text' : 'text-text-dim'), {}, a.name),
          el('span', 'text-[10px] uppercase tracking-wider', { style: `color:${TIER_COLOR[a.tier] ?? '#9a8a76'}` }, TIER_LABEL[a.tier] ?? a.tier),
          a.hidden && !a.unlocked ? el('span', 'text-[10px] text-text-dim/70', {}, '· hidden') : null),
        el('div', 'text-[12px] text-text-dim leading-snug mt-0.5', {}, a.description),
        progress),
      meta);
  }

  // ─── Repaint the whole list under the current filter ────────────────────────
  function paintList() {
    clear(list);
    const frag = document.createDocumentFragment();
    const cats = [...CATEGORY_ORDER, ...[...new Set(all.map((a) => a.category ?? 'other'))].filter((k) => !CATEGORY_ORDER.includes(k))];
    for (const cat of cats) {
      const inCat = all.filter((a) => (a.category ?? 'other') === cat);
      if (!inCat.length) continue;
      const done = inCat.filter((a) => a.unlocked).length;
      // Steam orders unlocked first (newest first), then the closest-to-done locked ones.
      const shown = inCat
        .filter((a) => filter === 'all' || (filter === 'unlocked') === a.unlocked)
        .sort((x, y) => {
          if (x.unlocked !== y.unlocked) return x.unlocked ? -1 : 1;
          if (x.unlocked) return (y.unlockedAt ?? '').localeCompare(x.unlockedAt ?? '');
          return (y.target ? y.progress / y.target : 0) - (x.target ? x.progress / x.target : 0);
        });
      if (!shown.length) continue;

      const section = el('section', 'flex flex-col gap-2');
      section.append(
        el('div', 'flex items-center gap-3 pb-1.5 border-b border-border', {},
          el('h4', 'font-heading text-gold/90 text-sm inline-flex items-center gap-1.5', {}, ...richText(CATEGORY_LABEL[cat] ?? cat)),
          el('span', 'text-[11px] font-mono text-text-dim', {}, `${done}/${inCat.length}`),
          el('div', 'flex-1'),
          el('div', 'w-24 h-1.5 rounded-full bg-surface-2 overflow-hidden border border-border', {},
            el('div', 'h-full bg-gold/60', { style: `width:${Math.round((done / inCat.length) * 100)}%` }))));
      for (const a of shown) section.append(row(a));
      frag.append(section);
    }
    if (!frag.childNodes.length) frag.append(el('div', 'text-sm text-text-dim py-6 text-center', {}, 'Nothing here yet.'));
    list.append(frag);
  }

  function filterBtn(id: Filter, label: string): HTMLElement {
    const b = el('button',
      'px-2.5 py-1 rounded-md border text-[12px] transition '
      + (filter === id ? 'border-gold text-gold bg-gold/10' : 'border-border text-text-dim hover:text-text'),
      { onClick: () => { filter = id; paintHeader(); paintList(); } }, label);
    return b;
  }

  let summary = { unlockedCount: 0, total: 0 };
  function paintHeader() {
    const pct = summary.total ? Math.round((summary.unlockedCount / summary.total) * 100) : 0;
    clear(header);
    header.append(
      el('div', 'flex items-center justify-between gap-3 mb-2 flex-wrap', {},
        el('h3', 'font-heading text-gold inline-flex items-center gap-1.5', {}, gicon('trophy', 16), 'Achievements'),
        el('div', 'flex items-center gap-1.5', {},
          filterBtn('all', 'All'), filterBtn('unlocked', 'Unlocked'), filterBtn('locked', 'Locked'),
          el('span', 'font-mono text-sm text-text-dim ml-1', {}, `${summary.unlockedCount} / ${summary.total} · ${pct}%`))),
      el('div', 'h-2.5 rounded-full bg-surface-2 overflow-hidden border border-border', {},
        el('div', 'h-full bg-gold transition-all', { style: `width:${pct}%` })));
  }
  paintHeader();

  achievementsApi.list().then((res) => {
    all = res.achievements;
    summary = { unlockedCount: res.unlockedCount, total: res.total };
    paintHeader();
    paintList();
  }).catch((e) => toast(e instanceof ApiError ? e.message : 'Failed to load achievements', 'error'));

  return () => {};
}
